import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import UploadField from '@/components/UI/UploadField';
import MediaInputField from '@/components/common/media/MediaInputField';
import MediaPreview from '@/components/common/media/MediaPreview';
import { ICategory } from '@/interfaces/category.interface';
import { IMediaProp } from '@/interfaces/mediaResponse.interface';
import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorMessage from '../../components/common/ErrorMessage';
import Breadcrumb from '@/components/common/Breadcrumb';
import { RoutesLink } from '@/utility/RouteLinks';
import { errorTypes } from '@/utility/validation';
import BtnLoader from '@/components/UI/loaders/BtnLoader';
import ProductDragDrop from '@/components/product/ProductDragDrop';

const EditCategory = () => {
  const forbiddenColors = ['#000000', '#ffffff'];
  const navigate = useNavigate();
  const [mediaFile, setMediaFile] = useState<IMediaProp | null>(null);
  const { id } = useParams();
  const categoryId = id;
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ICategory>({
    defaultValues: {
      name: '',
      colorCode: '',
      description: '',
      status: 'active',
      file: null,
    },
  });

  const onSubmit = async (data: ICategory) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('colorCode', data.colorCode);
      formData.append('description', data.description);
      formData.append('status', data.status);
      if (data.file) {
        formData.append('file', data.file);
      }
      let PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}`;
      if (categoryId) {
        PATH = `${PATH}/${categoryId}`;
      }
      await callApi(PATH, {
        method: categoryId ? 'PATCH' : 'POST',
        multipart: true,
        data: categoryId ? data : formData,
      });
      navigate(RoutesLink.categoryList);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      ThrowError(error);
    }
  };

  const handleDeleteMedia = async (id?: string) => {
    if (id) {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.media}/${id}`;
      const res = await callApi(PATH, { method: 'DELETE' });
      if (res) {
        setMediaFile(null);
      }
    } else {
      setMediaFile(null);
    }
  };

  const fetchCategoryInfo = async () => {
    try {
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/${categoryId}`;
      const res = await callApi(PATH, { method: 'GET' });
      reset({
        name: res.name,
        colorCode: res.colorCode,
        description: res.description,
        status: res.status,
        file: null,
      });

      if (res.media && res.media.length > 0) {
        setMediaFile({
          id: res.media[0].id,
          url: categoryId
            ? res.media[0].url
            : `${API_URL_PRODUCT_SERVICE}/${res.media[0].url}`,
          type: res.media[0].type,
          altText: res.media[0].altText || 'Category Image',
        });
      }
    } catch (err) {
      ThrowError(err);
    }
  };

  useEffect(() => {
    categoryId && fetchCategoryInfo();
  }, [categoryId, reset]);

  const breadcrumb = [
    { label: 'Categories', href: RoutesLink.categoryList },
    { label: 'Edit Category' },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="space-y-5">
            {/* About Category */}
            <h2 className="text-lg font-semibold mb-4">About Category</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Name"
                  type="text"
                  placeholder="Enter category name"
                  {...register('name')}
                />
              </div>
              <div>
                <FormField
                  label="Color Code"
                  type="text"
                  placeholder="eg. #f5045d"
                  {...register('colorCode', {
                    ...errorTypes.required('Color code'),
                    ...errorTypes.colorCode,
                    validate: {
                      forbidden: (value) =>
                        !forbiddenColors.includes(value.trim().toLowerCase()) ||
                        'This color is not allowed',
                    },
                  })}
                />
                <ErrorMessage message={errors.colorCode?.message} />
              </div>
            </div>

            <div>
              <FormField
                type="textarea"
                label="Description"
                placeholder="Start describing the category..."
                rows={4}
                {...register('description', {
                  ...errorTypes.required('description'),
                })}
              />
              <ErrorMessage message={errors.description?.message} />
            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Discount Type"
                  type="select"
                  options={[
                    { label: 'percentage', value: 'Percentage' },
                    { label: 'flat', value: 'Flat' },
                  ]}
                />
              </div>
              <div>
                <FormField
                  label="Discount Value"
                  type="text"
                  placeholder="Enter Value"
                // {...register('colorCode', {
                //   ...errorTypes.required('Color code'),
                //   ...errorTypes.colorCode,
                // })}
                />
              </div>
            </div> */}

            <div>
              <h4 className="block text-sm font-medium text-gray-700 mb-2">
                Upload Category Image
              </h4>

              {categoryId ? (
                <UploadField
                  referenceType="category"
                  referenceId={categoryId}
                  multiple={false}
                  initialMedia={mediaFile ? [mediaFile] : []}
                />
              ) : (
                <Controller
                  name="file"
                  control={control}
                  render={({ field }) => (
                    <MediaInputField
                      helperText="Upload category image"
                      multiple={false}
                      onFilesChange={(files) => {
                        if (files && files.length > 0) {
                          const file = files[0];
                          field.onChange(file);
                          setMediaFile({
                            url: URL.createObjectURL(file),
                            type: file.type.startsWith('video/')
                              ? 'video'
                              : 'image',
                            altText: file.name,
                          });
                        }
                      }}
                    />
                  )}
                />
              )}
              {mediaFile && !categoryId && (
                <div className="mt-4">
                  <MediaPreview
                    mediaUrls={[mediaFile]}
                    handleDeleteMedia={handleDeleteMedia}
                  />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="btn-secondary"
          >
            Cancel
          </button>
          <button disabled={isLoading} type="submit" className="btn-primary">
            {isLoading && <BtnLoader />} Save
          </button>
        </div>
      </form>
      <ProductDragDrop categoryId={categoryId as string} />
    </Layout>
  );
};

export default EditCategory;
